import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
} from '@mui/material';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { attendanceApi, notificationApi } from '../../services/employeeService';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone } from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatDate, formatDateTime, formatTime } from '../../utils/format';

interface Adjustment {
  id: string;
  reason: string;
  status: string;
  createdAt: string;
  sessionId?: string;
  staff?: { firstName: string; lastName: string };
}

interface AuditRow {
  id: string;
  action: string;
  reason?: string | null;
  createdAt: string;
  actor?: { email: string } | null;
  newValue?: { startedAt?: string; endedAt?: string; workDate?: string } | null;
}

const AUDIT_ACTION_LABEL: Record<string, string> = {
  'attendance.manual_edit': 'Korekta godzin',
  'attendance.manual_create': 'Ręczny wpis',
};

const REQUEST_STATUS: Record<string, { label: string; tone: PillTone }> = {
  PENDING: { label: 'Czeka na decyzję', tone: 'warning' },
  APPROVED: { label: 'Zatwierdzony', tone: 'success' },
  REJECTED: { label: 'Odrzucony', tone: 'danger' },
  NEEDS_INFO: { label: 'Wymaga uzupełnienia', tone: 'info' },
};

export const AdjustmentsPage: React.FC = () => {
  const [rows, setRows] = useState<Adjustment[]>([]);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [auditVisible, setAuditVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState<Adjustment | null>(null);
  const [note, setNote] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await attendanceApi.listAdjustments({}) as never);
      // Wykonane korekty żyją w dzienniku audytu (dostęp: właściciel/admin/księgowość)
      try {
        const logs = await notificationApi.auditLogs({ entity: 'AttendanceSession' }) as AuditRow[];
        setAuditRows(logs.filter((l) => AUDIT_ACTION_LABEL[l.action]));
      } catch {
        setAuditVisible(false);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doReview = async (status: string) => {
    if (!review) return;
    await attendanceApi.reviewAdjustment(review.id, { status, reviewNote: note });
    setReview(null); setNote(''); setToast('Zaktualizowano wniosek'); load();
  };

  const pending = rows.filter((r) => r.status === 'PENDING').length;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Zespół' }]}
        title="Korekty czasu pracy"
        description={!loading && pending > 0
          ? `${countLabel(pending, 'wniosek czeka', 'wnioski czekają', 'wniosków czeka')} na Twoją decyzję`
          : 'Wnioski pracowników o poprawienie godzin i historia zmian wprowadzonych w kalendarzu pracy.'}
      />

      <Stack gap={2.5}>
        {loading ? (
          <LoadingState variant="table" rows={3} />
        ) : (
          <SectionCard title="Wnioski o korektę" subtitle="Zgłoszone przez pracowników w ich panelu czasu pracy" padding="none">
            {rows.length === 0 ? (
              <EmptyState
                compact
                icon={<RuleOutlinedIcon />}
                title="Brak wniosków o korektę"
                description="Gdy pracownik zgłosi pomyłkę w swoich godzinach, wniosek pojawi się tutaj."
              />
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 680 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pracownik</TableCell>
                      <TableCell>Powód</TableCell>
                      <TableCell>Zgłoszono</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" aria-label="Akcje" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => {
                      const st = REQUEST_STATUS[r.status] || { label: r.status, tone: 'neutral' as PillTone };
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" gap={1.25}>
                              <PersonAvatar firstName={r.staff?.firstName} lastName={r.staff?.lastName} size={32} />
                              <Typography sx={{ fontWeight: 600, fontSize: 'inherit', whiteSpace: 'nowrap' }}>{r.staff?.firstName} {r.staff?.lastName}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 360 }}>{r.reason}</TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</TableCell>
                          <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                          <TableCell align="right">
                            {r.status === 'PENDING' && <Button size="small" variant="outlined" onClick={() => setReview(r)}>Rozpatrz</Button>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        )}

        {auditVisible && (loading ? (
          <LoadingState variant="table" rows={4} />
        ) : (
          <SectionCard title="Wykonane korekty i ręczne wpisy" subtitle="Z dziennika zmian: kto, kiedy i dlaczego zmienił godziny" padding="none">
            {auditRows.length === 0 ? (
              <EmptyState
                compact
                icon={<HistoryOutlinedIcon />}
                title="Brak wykonanych korekt"
                description="Korekty zapisane w kalendarzu pracy pojawią się tutaj."
              />
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data zmiany</TableCell>
                      <TableCell>Rodzaj</TableCell>
                      <TableCell>Wykonał(a)</TableCell>
                      <TableCell>Nowe godziny</TableCell>
                      <TableCell>Uzasadnienie</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {auditRows.map((l) => (
                      <TableRow key={l.id} hover>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDateTime(l.createdAt)}</TableCell>
                        <TableCell>
                          <StatusPill
                            label={AUDIT_ACTION_LABEL[l.action]}
                            tone={l.action === 'attendance.manual_create' ? 'info' : 'plum'}
                            noDot
                          />
                        </TableCell>
                        <TableCell sx={{ color: tokens.inkSoft }}>{l.actor?.email || '—'}</TableCell>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {l.newValue?.startedAt
                            ? `${formatDateTime(l.newValue.startedAt)}${l.newValue.endedAt ? ` – ${formatTime(l.newValue.endedAt)}` : ''}`
                            : '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>{l.reason || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        ))}
      </Stack>

      <Dialog open={!!review} onClose={() => setReview(null)} fullWidth maxWidth="sm">
        <DialogTitle>Rozpatrz wniosek o korektę</DialogTitle>
        <DialogContent>
          {review && (
            <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5, mb: 2.5 }}>
              <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 0.75 }}>
                <PersonAvatar firstName={review.staff?.firstName} lastName={review.staff?.lastName} size={28} />
                <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>{review.staff?.firstName} {review.staff?.lastName}</Typography>
                <Typography variant="caption" sx={{ ml: 'auto' }}>{formatDate(review.createdAt)}</Typography>
              </Stack>
              <Typography sx={{ color: tokens.ink, fontSize: '0.9375rem' }}>{review.reason}</Typography>
            </Box>
          )}
          <TextField
            label="Notatka dla pracownika"
            fullWidth
            multiline
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            helperText="Opcjonalnie — np. czego brakuje we wniosku albo jak poprawiono godziny."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setReview(null)} sx={{ mr: { sm: 'auto' } }}>Anuluj</Button>
          <Button variant="outlined" color="error" onClick={() => doReview('REJECTED')}>Odrzuć</Button>
          <Button variant="outlined" onClick={() => doReview('NEEDS_INFO')}>Poproś o uzupełnienie</Button>
          <Button variant="contained" onClick={() => doReview('APPROVED')}>Zatwierdź</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdjustmentsPage;
